/**
 * Pure font helpers: variant-key parsing/labelling, font-record merging, and
 * custom font upload validation. No store, API, or Vue access — safe to call
 * from services, composables, or components.
 */
import {
  SUPPORTED_FONT_FORMATS,
  WEIGHT_LABELS,
  WEIGHT_BY_NAME,
  getFontConfig
} from '@/constants/fonts';
import type {
  FontConfig,
  FontInfo,
  UploadedFont,
  GoogleFontRaw
} from '@/types/library/font';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * VARIANT KEYS  (regular / italic / 700 / 700italic ⇄ weight + style + label)
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Splits a variant key ("regular"/"italic"/"700"/"700italic") into weight + style. */
export function parseVariantKey(key: string): {
  weight: number;
  style: string;
} {
  if (key === 'regular') return { weight: 400, style: 'normal' };
  if (key === 'italic') return { weight: 400, style: 'italic' };
  const m = key.match(/^(\d+)(italic)?$/);
  if (m)
    return {
      weight: Number.parseInt(m[1]!, 10),
      style: m[2] ? 'italic' : 'normal'
    };
  return { weight: 400, style: 'normal' };
}

/** Human label for a variant key, e.g. "700italic" → "Bold Italic". */
export function variantLabel(key: string): string {
  if (key === 'regular') return 'Regular';
  if (key === 'italic') return 'Italic';
  const m = key.match(/^(\d+)(italic)?$/);
  if (m) {
    const base = WEIGHT_LABELS[m[1]!] ?? m[1]!;
    return m[2] ? `${base} Italic` : base;
  }
  return key;
}

/** Numeric CSS font-weight for a variant key, as a string. Defaults to "400". */
export function variantWeight(key: string): string {
  if (key === 'regular' || key === 'italic') return '400';
  const m = key.match(/^(\d+)/);
  return m ? m[1]! : '400';
}

/** Weight + style → variant key, the inverse of `parseVariantKey`. */
export function toVariantKey(weight: number, style: string): string {
  const italic = style === 'italic';
  if (weight === 400) return italic ? 'italic' : 'regular';
  return italic ? `${weight}italic` : String(weight);
}

/**
 * System and CDN families come from static config, not the font store, so
 * callers get a `FontInfo` built from that config — same shape as a Google or
 * uploaded font, so the pickers list their styles and apply their default
 * variant through one path. Cached by name because the catalogues never
 * change and stable objects keep the variant caches below warm.
 */
const staticFontInfos = new Map<string, FontInfo>();

export function staticFontInfo(fontFamily: string): FontInfo {
  const cached = staticFontInfos.get(fontFamily);
  if (cached) return cached;

  const config = getFontConfig(fontFamily);
  const info: FontInfo = {
    fontFamily,
    s3Url: config ? configVariantUrls(config) : {}
  };
  staticFontInfos.set(fontFamily, info);
  return info;
}

/**
 * Variant-key → file-URL map for a CDN or system family, so the picker can
 * list their styles the same way it lists a Google font's. System faces ship
 * with the browser and carry no file, so their keys map to an empty string.
 */
export function configVariantUrls(config: FontConfig): Record<string, string> {
  const urls: Record<string, string> = {};
  for (const variant of config.variants) {
    for (const style of variant.styles) {
      urls[toVariantKey(variant.value, style.style)] = style.src;
    }
  }
  return urls;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * GOOGLE FONT PARSING
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Light-parses a raw DeltaX Google-font record into a FontInfo. Only the fields
 * the catalogue list and filters need (family/tags/subsets/category) are copied;
 * the `s3Url`/`variantFiles` JSON strings are kept *unparsed* and resolved later
 * via `getS3Url` — applied only when a font is actually previewed/applied. This
 * keeps the first-load parse off the ~2000-font critical path.
 */
export function parseFont(raw: GoogleFontRaw): FontInfo | null {
  if (!raw.fontFamily) return null;
  return {
    fontId: raw.id,
    fontFamily: raw.fontFamily,
    category: raw.category,
    subsets: raw.subsets,
    tags: raw.tags,
    limitedCharsetSource: raw.limitedCharsetSource,
    rawS3Url: raw.s3Url,
    rawVariantFiles: raw.variantFiles
  };
}

/**
 * Variant-key → file-URL map for a font, parsed lazily and memoised.
 *
 * - Uploaded fonts already carry a parsed `s3Url` object → returned as-is.
 * - Google fonts keep raw JSON (`rawS3Url`, falling back to `rawVariantFiles`)
 *   which is parsed on first access and cached in a WeakMap, so we never parse
 *   a font we don't use, and never parse the same one twice.
 */
const s3UrlCache = new WeakMap<FontInfo, Record<string, string>>();
const defaultVariantCache = new WeakMap<FontInfo, string>();

export function pickDefaultVariant(font: FontInfo): string {
  const cached = defaultVariantCache.get(font);
  if (cached) return cached;
  const keys = Object.keys(getS3Url(font));
  const key = keys.includes('regular') ? 'regular' : (keys[0] ?? 'regular');
  defaultVariantCache.set(font, key);
  return key;
}

export function getS3Url(font: FontInfo): Record<string, string> {
  if (font.s3Url) return font.s3Url;
  const cached = s3UrlCache.get(font);
  if (cached) return cached;

  const rawJson = font.rawS3Url ?? font.rawVariantFiles;
  let parsed: Record<string, string> = {};
  try {
    if (rawJson) parsed = JSON.parse(rawJson) as Record<string, string>;
  } catch {
    console.warn(
      '[fontHelper] Failed to parse font variants:',
      font.fontFamily
    );
  }
  s3UrlCache.set(font, parsed);
  return parsed;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FONT RECORD MERGING
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Combine a font's list-endpoint record with its DB (?name=) record.
 * The Google list endpoint carries category/subsets/tags; the DB endpoint
 * carries s3Url/limitedCharsetSource. We overwrite with any field the DB
 * record actually provides and keep the existing value for fields it omits,
 * so an applied font keeps the metadata that tag/subset filters rely on.
 */
export function mergeFontData(
  existingFont: FontInfo | undefined,
  incomingFont: FontInfo
): FontInfo {
  if (!existingFont) return incomingFont;
  const nonNullIncomingFields = Object.fromEntries(
    Object.entries(incomingFont).filter(
      ([, value]) => value !== undefined && value !== null
    )
  );
  return { ...existingFont, ...nonNullIncomingFields } as FontInfo;
}

/**
 * Folds one uploaded font face into a family-grouped FontInfo row, adding its
 * variant (derived from the sub-family) to the family's s3Url map. Mutates the
 * passed map in place.
 */
export function mergeUploadedFace(
  target: Map<string, FontInfo>,
  face: UploadedFont
): void {
  const variantKey = subFamilyToVariantKey(face.fontSubFamily);
  const existing = target.get(face.fontFamily);
  if (existing) {
    existing.s3Url = { ...existing.s3Url, [variantKey]: face.url };
  } else {
    target.set(face.fontFamily, {
      fontFamily: face.fontFamily,
      s3Url: { [variantKey]: face.url },
      isUploaded: true,
      tags: {},
      subsets: []
    });
  }
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * UPLOAD VALIDATION & METADATA
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** File extension (lowercased) without the dot, or '' when absent. */
export function fileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
}

/** Name without its extension, used as a fallback when metadata can't be read. */
export function fileNameWithoutExtension(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '');
}

/**
 * Whether a file looks like a supported font, judged by extension. Browsers
 * report most font files as `application/octet-stream`, so MIME is unreliable
 * here — `validateFontFile` is the real gate.
 */
export function isSupportedFontFormat(file: File): boolean {
  const ext = fileExtension(file.name);
  return ext.length > 0 && SUPPORTED_FONT_FORMATS.extensions.has(ext);
}

/**
 * Confirms the browser can actually decode the file as a font. Doubles as a
 * corruption check before we upload.
 */
export async function validateFontFile(file: File): Promise<boolean> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const face = new FontFace(
      `__font_validate_${Date.now()}`,
      `url(${objectUrl})`
    );
    await face.load();
    return true;
  } catch {
    return false;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Maps a font sub-family string ("Bold", "Semi Bold Italic", …) to the variant
 * key shape used elsewhere ("regular", "italic", "700", "700italic"), so
 * uploaded fonts slot into the same variant UI as Google Fonts.
 */
export function subFamilyToVariantKey(subFamily: string): string {
  const normalized = subFamily.trim().toLowerCase();
  const isItalic =
    normalized.includes('italic') || normalized.includes('oblique');
  const weightPart = normalized
    .replace(/italic|oblique/g, '')
    .replace(/\s+/g, '')
    .trim();
  const weight = WEIGHT_BY_NAME[weightPart] ?? 400;
  if (weight === 400) return isItalic ? 'italic' : 'regular';
  return isItalic ? `${weight}italic` : `${weight}`;
}
